import React, { Component } from 'react';

const RELOAD_FLAG = 'micoach:chunkReload';

// Heurística para detectar errores de chunks no encontrados tras un redeploy.
// Cuando el bundle viejo cacheado del navegador intenta hacer lazy-load de
// un chunk con un hash que ya no existe en el servidor, lanza ChunkLoadError.
// La cura segura es forzar un reload para descargar el bundle nuevo.
function isChunkLoadError(error) {
  if (!error) return false;
  const name = error.name || '';
  const msg = error.message || '';
  return (
    name === 'ChunkLoadError' ||
    /Loading chunk [\w\d]+ failed/i.test(msg) ||
    /Loading CSS chunk/i.test(msg) ||
    /Failed to fetch dynamically imported module/i.test(msg)
  );
}

class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    this.setState({ errorInfo });
    console.error('ErrorBoundary caught:', error, errorInfo);

    // Si es un chunk perdido por redeploy, intentamos un único reload
    // automático. El flag en sessionStorage evita bucle infinito si tras
    // recargar sigue fallando (por otra causa).
    if (isChunkLoadError(error)) {
      try {
        const alreadyReloaded = sessionStorage.getItem(RELOAD_FLAG) === '1';
        if (!alreadyReloaded) {
          sessionStorage.setItem(RELOAD_FLAG, '1');
          window.location.reload();
          return;
        }
      } catch {
        // sessionStorage puede no estar disponible (modo privado iOS, etc.)
        window.location.reload();
        return;
      }
    }
  }

  componentDidMount() {
    // Si el reload anterior fue exitoso (estamos montados sin error), limpiamos
    // el flag para que futuros redeploys puedan disparar otro reload.
    try {
      if (sessionStorage.getItem(RELOAD_FLAG) === '1' && !this.state.hasError) {
        sessionStorage.removeItem(RELOAD_FLAG);
      }
    } catch {}
  }

  render() {
    if (this.state.hasError) {
      const chunkErr = isChunkLoadError(this.state.error);
      return (
        <div className="error-fallback">
          <div className="w-16 h-16 rounded-2xl flex items-center justify-center mb-4"
            style={{ background: 'rgba(255,77,109,0.1)' }}>
            <span className="text-3xl">⚠️</span>
          </div>
          <h2 className="text-xl font-bold mb-2" style={{ fontFamily: 'Inter, sans-serif' }}>
            {chunkErr ? 'Nueva versión disponible' : 'Algo salió mal'}
          </h2>
          <p className="text-zinc-500 text-sm mb-4 max-w-md">
            {chunkErr
              ? 'Hubo una actualización del frontend. Refresca la página para cargar la versión más reciente.'
              : (this.state.error?.message || 'Error inesperado en esta sección.')}
          </p>
          <div className="flex gap-2">
            <button
              onClick={() => {
                try { sessionStorage.removeItem(RELOAD_FLAG); } catch {}
                window.location.reload();
              }}
              className="px-4 py-2 rounded-lg text-sm font-medium"
              style={{ background: 'var(--primary)', color: 'var(--primary-ink)' }}
            >
              Recargar
            </button>
            {!chunkErr && (
              <button
                onClick={() => this.setState({ hasError: false, error: null })}
                className="px-4 py-2 rounded-lg text-sm font-medium"
                style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid var(--border-strong)', color: 'var(--text-1)' }}
              >
                Intentar de nuevo
              </button>
            )}
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

export default ErrorBoundary;
